import type { BaseAddress } from '@/types/Address'
import type { MultilinkStoryblok } from '@/types/Storyblok'
import type { AstroGlobal } from 'astro'
import * as cheerio from 'cheerio'

/**
 * Converts a Unix timestamp to a JavaScript Date object.
 * @param timestamp - The Unix timestamp to convert.
 * @returns The Date object representing the given timestamp.
 */
export const unixTimestampToDate = (timestamp: number): Date => new Date(timestamp * 1000)

/**
 * Converts a Unix timestamp to an ISO string.
 * @param timestamp
 * @param timestamp.timestamp - The Unix timestamp to convert.
 * @param timestamp.includeTime - Whether to include the time in the ISO string. Default is false.
 * @returns The converted ISO string.
 */
export function unixTimestampToIsoString({ timestamp, includeTime = false }: { timestamp: number, includeTime?: boolean }): string {
  const date = unixTimestampToDate(timestamp)
  const isoString = date.toISOString()
  return includeTime ? isoString : isoString.split('T')[0]
}

/**
 *
 * @param date date input as Date object or unix timestamp
 * @returns formated date string - e.g. 1. Januar 2021
 */
export function formatDate(date: Date | number): string {
  if (date !== 0 && typeof date === 'number') {
    date = unixTimestampToDate(date)
  }
  return new Intl.DateTimeFormat('de-CH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Europe/Zurich',
  }).format(date)
}

/**
 * Combines attribute values from an item object based on the provided attributes.
 *
 * @param item - The item object containing the attributes.
 * @param attributes - An array of attribute names to combine.
 * @returns An object with the combined attribute values and a string representation of the combined values.
 */
export function combineAttributeValues<T, K extends keyof T>(item: T, attributes: (K | `${Extract<keyof T, string>}&${Extract<keyof T, string>}`)[]): { lines: (string | T[K])[], line: string } {
  const elements = attributes
    .map((attr) => {
      if (typeof attr === 'string' && attr.includes('&')) {
        const attrs = attr.split('&') as K[]
        return attrs.map((a) => {
          return item[a]
        }).join(' ')
      }
      else {
        return item[attr as K]
      }
    })
    .filter(el => el && (el as string).trim().length !== 0)
  return { lines: elements, line: elements.join(', ') }
}

/**
 * Shortens a given link by extracting the protocol and hostname.
 * If the link is not a valid URL, it returns the original link.
 *
 * @param link - The link to be shortened.
 * @returns The shortened link or the original link if it is not a valid URL.
 */
export function shortenLink(link: string) {
  try {
    const url = new URL(link)
    return `${url.protocol}//${url.hostname}`
  }
  // eslint-disable-next-line unused-imports/no-unused-vars
  catch (error) {
    return link
  }
}

/**
 * Removes HTML tags from a given text.
 *
 * @param text - The text containing HTML tags.
 * @returns The text with HTML tags removed.
 */
export function removeHTMLTags(text: string): string {
  return cheerio.load(text).text()
}

/**
 * Computes the workload based on the minimum and maximum percentages.
 *
 * @param percent_min The minimum percentage.
 * @param percent_max The maximum percentage.
 * @returns The computed workload as a string.
 */
export function computeWorkload(percent_min: number, percent_max: number): string {
  if (percent_min === percent_max) {
    return `${percent_min}%`
  }
  return `${percent_min}% - ${percent_max}%`
}

/**
 * Truncates a string if it exceeds a specified threshold.
 *
 * @param input - The string to truncate.
 * @param threshold - The maximum length of the string before truncation.
 * @returns The truncated string.
 */
export function truncateString(input: string, threshold: number): string {
  const trimmed = input.trim()
  if (trimmed.length > threshold) {
    return `${trimmed.substring(0, threshold).trim()}...`
  }

  return input
}

/**
 * Converts a string to a URL-safe string by encoding it and converting it to lowercase.
 * @param str - The string to be converted.
 * @returns The URL-safe string.
 */
export function toUrlSaveString(str: string) {
  return encodeURIComponent(str.toLocaleLowerCase())
}

/**
 * Converts a url safe string back to a normal string.
 * @param str - The url safe string to be converted.
 * @returns The normal string.
 */
export function fromUrlSaveString(str: string) {
  return decodeURIComponent(str)
}

/**
 * Generates a isomorphic Google Maps link for a given address.
 * Opens the Google Maps website or app if available.
 * @param item - The address to generate the link for.
 * @returns The Google Maps link.
 */
export function generateGoogleMapsLink<TItem extends BaseAddress>(item: TItem): string {
  const addressString = combineAttributeValues<TItem, keyof TItem>(item, [
    // @ts-expect-error - This is a valid key
    'location_street&location_street_number',
    // @ts-expect-error - This is a valid key
    'location_zip&location_city',
  ]).line

  if (addressString.length === 0) {
    return ''
  }
  return `https://www.google.com/maps/search/?api=1&query=${toUrlSaveString(addressString)}`
}

/**
 * Checks if two slugs are the same.
 *
 * @param slugs - The slugs to compare.
 * @param slugs.requestSlug - The slug from the request.
 * @param slugs.dataSlug - The slug from the data.
 * @returns A boolean indicating whether the slugs are the same.
 */
export const isSameSlug = ({ requestSlug, dataSlug }: { requestSlug: string, dataSlug: string }): boolean => requestSlug === dataSlug

/**
 * Replaces German umlauts (ä, ö, ü, Ä, Ö, Ü) and the German sharp s (ß) with their respective ASCII equivalents.
 * @param str - The input string to be processed.
 * @returns The input string with umlauts and sharp s replaced by their ASCII equivalents.
 */
export function replaceUmlauts(str: string): string {
  return str
    .replace(/ß/g, 'ss')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae')
    .replace(/Ö/g, 'Oe')
    .replace(/Ü/g, 'Ue')
}

/**
 * Extracts the YouTube video ID from a given input string.
 * @param input - The input string which can be a YouTube video URL or video ID.
 * @returns The YouTube video ID extracted from the input string.
 */
export function getYoutubeVideoId(input: string): string {
  const maybeUrlOrYtId = input.trim()

  try {
    const videoUrl = new URL(maybeUrlOrYtId)

    // if video url like https://www.youtube.com/watch?v=FBWxEywo85Q
    let videoId = videoUrl.searchParams.get('v')

    if (videoId) {
      // Remove any additional parameters
      const ampersandPosition = videoId.indexOf('&')
      if (ampersandPosition !== -1) {
        videoId = videoId.substring(0, ampersandPosition)
      }

      return videoId
    }

    const pathname = videoUrl.pathname.split('/')

    // if share url like https://youtu.be/FBWxEywo85Q?si=2q2h7N3k5aqHC0pT
    // or embed url like https://www.youtube.com/embed/FBWxEywo85Q
    return pathname.includes('embed') ? pathname[2] : pathname[1]
  }
  // eslint-disable-next-line unused-imports/no-unused-vars
  catch (error) {
    // Invalid URL, return original input, most likely a video id
    return maybeUrlOrYtId
  }
}

/**
 * Extracts the Vimeo video ID from a given input string.
 * @param input
 * @returns The Vimeo video ID extracted from the input string.
 */
export function getVimeoVideoId(input: string): string {
  const maybeUrlOrVimeoId = input.trim()
  try {
    const videoUrl = new URL(maybeUrlOrVimeoId)
    const pathname = videoUrl.pathname.split('/')
    return pathname.pop() || ''
  }
  catch {
    // Invalid URL, return original input, most likely a video id
    return maybeUrlOrVimeoId
  }
}

/**
 * Gets the video provider from a video URL.
 * @param input
 * @returns 'youtube' | 'vimeo' | 'unknown'
 */
export function getVideoProviderFromVideoUrl(input: string): 'youtube' | 'vimeo' | 'unknown' {
  if (input.includes('youtube') || input.includes('youtu.be')) {
    return 'youtube'
  }
  else if (input.includes('vimeo')) {
    return 'vimeo'
  }
  else {
    return 'unknown'
  }
}

/**
 * IMPORTANT: Altough Netlify sets the variable "CONTEXT" in its evnironment, it is not available in server islands.
 * We set the same variable valuse in the custom "DEPLOY_CONTEXT" variable. Do not add it to the local .env file, because
 * we detect some features of Netlify envs by the presence of this variable. If the app is run with Astro dev server, these
 * features need to be disabled, as Netlify does not work in this context.
 */

/**
 * Determines if the current deployment is a production deployment.
 *
 * @returns {boolean} Returns true if the deployment is a production deployment, otherwise false.
 */
export function isProductionDeployment(): boolean {
  // CONTEXT should be available in the Netlify environment, but server island requests somehow do not have
  // access to this environment variable. Therefore, we also check DEPLOY_CONTEXT which is a custom variable.
  // https://docs.netlify.com/configure-builds/environment-variables/#build-metadata
  return import.meta.env.DEPLOY_CONTEXT === 'production'
}
export const isDeployPreviewDeployment = (): boolean => import.meta.env.DEPLOY_CONTEXT === 'deploy-preview'
export const isBranchDeployment = (): boolean => import.meta.env.DEPLOY_CONTEXT === 'branch-deploy'
export const isDev = (): boolean => import.meta.env.DEPLOY_CONTEXT === 'dev'
export const isProdDomain = (): boolean => import.meta.env.URL === 'https://www.sozialinfo.ch'

/**
 * Checks if the code is running in a Netlify context.
 * @returns {boolean} Returns true if running in a Netlify context, otherwise false.
 */
export function isNetlifyContext(): boolean {
  return !!import.meta.env.DEPLOY_CONTEXT
}

/**
 * Checks if a value is not null. Can be used as a type guard.
 *
 * @param v - The value to check.
 * @returns A boolean indicating whether the value is not null.
 */
export function isNotNull<T>(v: T | null): v is T {
  return v !== null
}

/**
 * Returns MultilinkStoryblok href based on its type
 *
 * @param link - The MultilinkStoryblok object
 * @returns The href of the MultilinkStoryblok
 */
export function getMultilinkStoryblokHref(link: MultilinkStoryblok): string {
  // https://www.storyblok.com/docs/guide/in-depth/rendering-the-link-field
  switch (link.linktype) {
    case 'email':
      return `mailto:${link.email}` || '/'
    case 'story':
      return `/${link.cached_url || ''}`
    case 'url':
    case 'asset':
    default:
      return link.url || link.cached_url || '/'
  }
}

/**
 * Determines rendering of a layout block based on the provided component type and position.
 *
 * @param component - The block object containing the component type.
 * @returns A boolean indicating whether the block should be rendered.
 */
export function renderLayoutBlock(component: string): boolean {
  // Do not render default block layout for these components, as they have a background color and
  // handle spacing internally.
  if (component === 'grid-container' || component === 'post_list') {
    return false
  }

  // for all other components, render the default block layout
  return true
}

/**
 * Gets the extension of a file from a given string (filename).
 * @param filename - The filename to extract the extension from.
 * @returns The extension of the file.
 */
export function getFileExtension(filename: string): string {
  const maybeFileWithExtension = filename.split('.').length > 1

  if (!maybeFileWithExtension) {
    return ''
  }

  return filename.split('.').pop() || ''
}

/**
 * Checks if a given file is an gif based on its extension.
 * @param filename - The filename to check.
 * @returns A boolean indicating whether the file is an image.
 */
export function isGif(filename: string): boolean {
  return getFileExtension(filename).toLowerCase() === 'gif'
}

/**
 * Removes empty trailing paragraphs from the html.
 * Depends on newlines to separate paragraphs.
 * @param html - The html to process.
 * @returns The html without empty trailing paragraphs.
 */
export function removeTrailingParagraphs(html: string): string {
  try {
    // Split the text into lines
    const lines = html.split('\n')

    // Match paragraphs containing only:
    // - HTML tags (includes comments)
    // - whitespace
    // - &nbsp;
    const emptyParaPattern = /^<p>(?:<[^>]*>|&nbsp;|\s)*<\/p>$/

    // iterate from the end of the array to find the last index of a non-empty paragraph
    let lastContentIndex = lines.length - 1
    while (lastContentIndex >= 0 && emptyParaPattern.test(lines[lastContentIndex].trim())) {
      lastContentIndex--
    }

    // slice the array to remove the empty trailing paragraphs
    return lines.slice(0, lastContentIndex + 1).join('\n')
  }
  catch {
    return html
  }
}

/**
 * Redirects the request to a URL with a trailing slash if it doesn't already have one.
 *
 * @param {AstroGlobal} ctx - The context object containing the request URL and redirect method.
 * @returns {Response | void} - A redirect response to the URL with a trailing slash, or void if the URL already has a trailing slash.
 */
export function redirectToTrailingSlash(ctx: AstroGlobal): Response | void {
  const search = ctx.url.search ? `${ctx.url.search}` : ''

  if (!ctx.url.pathname.endsWith('/')) {
    return ctx.redirect(`${ctx.url.pathname}/${search}`, search ? 302 : 301)
  }
}
